/**
 * Format a date string for display in billing/report contexts.
 *
 * Date-only strings (YYYY-MM-DD) are rendered in UTC to avoid the classic
 * off-by-one bug where "2026-04-10" parses as midnight UTC and then renders
 * as "Apr 9" in Pacific time. Full ISO timestamps are rendered in the
 * browser's local timezone.
 */
export function formatBillingDate(ts: string | null | undefined): string {
  if (!ts) return ""
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(ts)
  const date = new Date(ts)
  if (Number.isNaN(date.getTime())) return ts
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(isDateOnly ? { timeZone: "UTC" } : {}),
  })
}

/**
 * Whole days from `from` to `to` (defaults to now). UTC-safe for YYYY-MM-DD
 * inputs (both parse as UTC midnight, so the diff is exact). Returns null for
 * missing/invalid input.
 */
export function daysBetween(
  from: string | null | undefined,
  to?: string | null
): number | null {
  if (!from) return null
  const start = new Date(from)
  const end = to ? new Date(to) : new Date()
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  const MS_PER_DAY = 24 * 60 * 60 * 1000
  return Math.floor((end.getTime() - start.getTime()) / MS_PER_DAY)
}

/** Whole days since a date. Null if missing/invalid. */
export function daysSince(date: string | null | undefined): number | null {
  return daysBetween(date, undefined)
}

/**
 * Human "how long ago" label for a timestamp: "today", "yesterday",
 * "5 days ago" — or the compact "today" / "5d ago" with `style: "short"`.
 * Future dates clamp to "today". Returns null for missing/invalid input.
 */
export function daysAgoLabel(
  date: string | null | undefined,
  opts?: { style?: "long" | "short" }
): string | null {
  const days = daysSince(date)
  if (days == null) return null
  const n = Math.max(0, days)
  if (opts?.style === "short") return n === 0 ? "today" : `${n}d ago`
  if (n === 0) return "today"
  if (n === 1) return "yesterday"
  return `${n} days ago`
}

/** Compact chat-list age: "just now", "5m", "3h", "2d", then a short date. */
export function formatRelativeTime(dateStr: string) {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString([], { month: "short", day: "numeric" })
}

/** Job-board posting age: "Today", "1 day ago", "12 days ago", capped at "60+ days ago". */
export function formatDaysAgo(dateString: string): string {
  const now = new Date()
  const posted = new Date(dateString)
  const diffMs = now.getTime() - posted.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays > 60) return "60+ days ago"
  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "1 day ago"
  return `${diffDays} days ago`
}

const BILLING_TIME_ZONE = "America/Denver"

/** Hire date in the billing time zone; an em dash when missing/invalid. */
export function formatHiredOn(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: BILLING_TIME_ZONE,
  })
}

const MOUNTAIN_TIME = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: BILLING_TIME_ZONE,
})

const mountainParts = (date: Date) =>
  Object.fromEntries(MOUNTAIN_TIME.formatToParts(date).map((p) => [p.type, p.value]))

/** "Oct 8" in Mountain Time, with the year when it is not this year. Empty when missing/invalid. */
export function formatMountainDay(iso: string | null | undefined): string {
  const date = new Date(iso ?? "")
  if (Number.isNaN(date.getTime())) return ""
  const at = mountainParts(date)
  const year = at.year === mountainParts(new Date()).year ? "" : `, ${at.year}`
  return `${at.month} ${at.day}${year}`
}

/**
 * "11:30 AM MT" in Mountain Time, with the day in front ("Oct 8, 11:30 AM MT") when
 * that is not today there. The zone is named, as in formatMountainTime. Empty when
 * missing/invalid.
 */
export function formatMountainClock(iso: string | null | undefined): string {
  const date = new Date(iso ?? "")
  if (Number.isNaN(date.getTime())) return ""
  const at = mountainParts(date)
  const now = mountainParts(new Date())
  const clock = `${at.hour}:${at.minute} ${at.dayPeriod} MT`
  const today = at.year === now.year && at.month === now.month && at.day === now.day
  return today ? clock : `${formatMountainDay(iso)}, ${clock}`
}

/**
 * A time for the team, always in Mountain Time with the zone named:
 * "Oct 6, 3:47 PM MT", with the year when it is not this year. Empty when
 * missing/invalid.
 */
export function formatMountainTime(iso: string | null | undefined): string {
  const date = new Date(iso ?? "")
  if (Number.isNaN(date.getTime())) return ""
  // Put together from the parts: the formatter's own string has a narrow
  // no-break space before AM/PM.
  const at = mountainParts(date)
  const year = at.year === mountainParts(new Date()).year ? "" : `, ${at.year}`
  return `${at.month} ${at.day}${year}, ${at.hour}:${at.minute} ${at.dayPeriod} MT`
}
